import type { PosthogEventType } from "@repo/app-types/client/client";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { useCallback } from "react";
import { useAppSelector } from "../../hooks/hook.js";
import { captureAnalyticsEvent } from "../analytics/analyticsClient.js";
import { selectTeammate } from "./teammateSlice.js";

export type ViewerEventProperties = Record<string, string | boolean | null>;

interface ViewerAnalytics {
  /** The signed-in teammate mirrored by PostHog's first-class user identity. */
  viewerId: TeammateId;
  /**
   * Calls the PostHog SDK with `event` stamped with the viewer's role. PostHog
   * supplies the actor identity itself; no viewer id is added to event props.
   */
  captureWithViewer: (
    event: PosthogEventType,
    properties: ViewerEventProperties,
  ) => void;
}

/**
 * PostHog owns who is operating the app. This hook reads the same resolved
 * teammate only for event-time derived properties such as role and self-view;
 * analytics contexts remain reserved for where the viewer is in the app.
 */
export function useViewerAnalytics(): ViewerAnalytics {
  const viewer = useAppSelector(selectTeammate);
  if (!viewer) {
    throw new Error("Viewer analytics require the signed-in teammate");
  }
  const viewerId = viewer.id;
  const viewerRole = viewer.permissions.role.type;

  const captureWithViewer = useCallback<ViewerAnalytics["captureWithViewer"]>(
    (event, properties) => {
      const eventProperties = { ...properties, viewer_role: viewerRole };

      try {
        captureAnalyticsEvent(event, eventProperties);
      } catch (error) {
        console.error("Failed to capture analytics event", error);
      }
    },
    [viewerRole],
  );

  return { viewerId, captureWithViewer };
}
